import Hls from 'hls.js';
import { createMachine, assign } from 'xstate';
import { choose, sendParent, raise } from 'xstate/lib/actions';

import { progressFetch } from '@sg-core/resources/assets/js/libs/progress-fetch';

import type Plyr from 'plyr';

interface LoadingSourcesContext {
  abortController: AbortController | null;
  hls: Hls | null;
  plyr: Plyr | null;
  posterSrc: string | null;
  prefetch: boolean;
  sourceElement:
    | ((HTMLSourceElement | HTMLVideoElement) & {
        dataset: DOMStringMap & { currentSrc?: string };
        src?: string;
      })
    | null;
  videoElement: HTMLVideoElement | HTMLIFrameElement | null;
  videoSrc: string | null;
}

interface LoadingSourcesEvent {
  type: string;
  [key: string]: unknown;
}

export const loadingSourcesMachine = createMachine<LoadingSourcesContext, LoadingSourcesEvent>(
  {
    id: 'loading',
    predictableActionArguments: true,
    preserveActionOrder: true,

    initial: 'pending',

    context: {
      abortController: null,
      hls: null,
      plyr: null,
      posterSrc: null,
      prefetch: false,
      sourceElement: null,
      videoElement: null,
      videoSrc: null,
    },

    states: {
      /**
       * Use a delayed transition here to actually trigger a state change event when
       * entering the `load` node. Since everything else is defined as immediate
       * transition (`always`) with synchronous tasks the machine doesn't trigger
       * a change because it wont rest on that state longer than an event loop iteration.
       * 250ms is a value long enough to visually respond to the change via a `CSS`
       * class transition but short enough to not interfere with the user experience.
       */
      pending: {
        after: {
          250: { target: 'preflightCheck' },
        },
      },

      /**
       * Performs checks to determine if the video is playable
       * and prepare sources elements if required
       */
      preflightCheck: {
        always: [
          {
            /**
             * If `videoElement` is not an instance of `HTMLVideoElement` and
             * we have a `plyr` instance, we are probably playing a third party
             * video service source, such as Youtube. The preflight check must
             * pass to delegate the operation to the `Plyr` player.
             */
            target: 'ready',
            cond: 'isThirdPartySource',
          },
          {
            /**
             * The `networkState` seems to indicate we don't have a selected source yet
             * but the `videoSrc` is not defined which probably means there is
             * an issue in the lazy / responsive source selection.
             */
            actions: 'handleError',
            cond: 'hasNoSource',
          },
          {
            /**
             * It seems there is a source selected but the lazy / responsive logic
             * has not been executed. We are in an "eager" loading scenario and
             * can safely resolve since the browser is autonomous from here.
             */
            target: 'ready',
            cond: 'hasEagerlyLoaded',
          },
          {
            /**
             * The source has not changed and the video has already been processed.
             * We are probably in a show / hide sequence due to the IntersectionObserver,
             * or a play / pause sequence triggered by the user.
             */
            target: 'ready',
            cond: 'isAlreadyLoaded',
          },
          {
            /**
             * All is good so far but the video is not yet ready to be played
             */
            target: 'processSource',
          },
        ],
      },

      /**
       * Delegate the processing of the `videoSrc` depending
       * on the current context we are in (hls, prefetch, etc.)
       */
      processSource: {
        initial: 'pending',
        entry: 'selectSourceType',

        on: {
          HLS: {
            target: '.handleHls',
          },

          PREFETCH: {
            target: '.handlePrefetch',
          },

          DONE: {
            target: '#ready',
            actions: 'updateSourceElement',
          },
        },

        states: {
          pending: {},

          handleHls: {
            invoke: {
              src: 'handleHls',
            },

            on: {
              /**
               * The current client handle HLS source natively (mainly iOS
               * for the moment). We just need to set the source in the DOM
               * and let the platform handle it
               */
              NATIVE: {
                target: '#ready',
                actions: 'useNativeHls',
              },

              /**
               * The current client doesn't handle HLS source natively.
               * We kick in the third party polyfill and let it handle
               * required DOM manipulations
               */
              POLYFILL: {
                target: '#ready',
                actions: 'useHlsPolyfill',
              },
            },
          },

          /**
           * Fetch a source in a `blob` object to be used as prefetched video source.
           * We use an `AbortController` to control fetched requests
           */
          handlePrefetch: {
            entry: ['abortSignal', 'initController'],

            invoke: {
              src: 'prefetch',
              onDone: {
                target: '#ready',
                actions: 'updateSourceElement',
              },
            },
          },
        },
      },

      /**
       * The final state of the machine. Indicate that sources has been correctly
       * loaded and are ready to be played
       */
      ready: {
        id: 'ready',
        type: 'final',
        data: (context) => context,
      },
    },
  },
  {
    guards: {
      /**
       * Very basic check to determine if we are relying on a third party
       * video service such as Youtube (`videoElement` will be an `iFrame`
       * in that case)
       */
      isThirdPartySource: ({ videoElement, plyr }) =>
        !!videoElement && !(videoElement instanceof HTMLVideoElement) && Boolean(plyr),

      /**
       * Check if the video element is in a `NETWORK_NO_SOURCE` state
       * without having a possible `videoSrc` to provide
       */
      hasNoSource: ({ videoElement, videoSrc }) =>
        videoElement instanceof HTMLVideoElement && videoElement.networkState === 3 && !videoSrc,

      /**
       * Check if the video element is already in a playable state
       * and we don't have a `videoSrc` value. That probably means
       * `<source>` element was provided a `src` attribute directly.
       */
      hasEagerlyLoaded: ({ videoElement, videoSrc }) =>
        videoElement instanceof HTMLVideoElement &&
        videoElement.networkState > 0 &&
        videoElement.networkState < 3 &&
        !videoSrc,

      /**
       * Check if videoSrc is already the current video source and the
       * ready state indicate the video element has already at least
       * some meta data about the source
       */
      isAlreadyLoaded: ({ videoElement, videoSrc, sourceElement }) =>
        Boolean(sourceElement?.dataset.currentSrc === videoSrc) &&
        videoElement instanceof HTMLVideoElement &&
        videoElement.readyState > 0,

      /**
       * Check if the URL provided point on an HLS playlist resource
       */
      isHlsSrc,

      /**
       * Check if we need to prefetch the entire source before playing it
       */
      requirePrefetch: ({ prefetch }) => prefetch,
    },
    actions: {
      /**
       * Performs the right action based on source type
       */
      selectSourceType: choose([
        {
          /**
           * When an `hls` source is used, we have a videoSrc defined but
           * we delegate the loading operation to the third party library
           */
          actions: raise('HLS'),
          cond: 'isHlsSrc',
        },
        {
          /**
           * When `prefetch` is used, we want to fully load the video file
           * in a `blob` object and bypass any browser stream mechanism
           */
          actions: raise('PREFETCH'),
          cond: 'requirePrefetch',
        },
        {
          /**
           * In a conventional scenario, we just update the `src` attribute of
           * the `<source>` element with the correct resource URL
           */
          actions: raise('DONE'),
        },
      ]),

      /**
       * Assign the HLS polyfill instance to the context for later reference
       */
      useHlsPolyfill: assign({
        hls: (_, event) => ('hlsInstance' in event ? (event.hlsInstance as Hls) : null),
      }),

      /**
       * AbortController is used to cancel ongoing fetch request in
       * `prefetch` source scenarios
       */
      initController: assign({ abortController: () => new AbortController() }),

      /**
       * Abort any potential pending request before making a new one
       */
      abortSignal({ abortController }) {
        abortController?.abort();
      },

      /**
       * Update the DOM with the current resource URI (may be URL or a blob object)
       */
      updateSourceElement({ sourceElement, videoElement, videoSrc }, { data }) {
        if (!sourceElement) {
          return;
        }

        const nextSrc = (data as string | null) ?? videoSrc;

        if (!nextSrc) {
          return;
        }

        if (videoElement instanceof HTMLVideoElement) {
          videoElement.removeAttribute('src');
          delete videoElement.dataset.currentSrc;
        }

        if ('src' in sourceElement) {
          sourceElement.setAttribute('src', nextSrc);
        }

        /**
         * `sourceElement.src` can be a blob object but we want to keep trace of the actual
         * source string use to create the blob
         */
        sourceElement.dataset.currentSrc = videoSrc ?? nextSrc;
      },

      /**
       * Native HLS support is exposed on the `<video>` element itself, and
       * Safari is more reliable when the playlist URL is assigned there.
       */
      useNativeHls({ sourceElement, videoElement, videoSrc }, { data }) {
        if (!(videoElement instanceof HTMLVideoElement)) {
          return;
        }

        const nextSrc = (data as string | null) ?? videoSrc;

        if (!nextSrc) {
          return;
        }

        videoElement.setAttribute('src', nextSrc);
        videoElement.dataset.currentSrc = nextSrc;

        if (sourceElement) {
          sourceElement.removeAttribute('src');
          sourceElement.dataset.currentSrc = nextSrc;
        }
      },

      handleError: sendParent('ERROR'),
    },
    services: {
      /**
       * Handle HLS specific sources by instantiating the HLS library. If the hls
       * polyfill is not supported, we delegate back the source to the native
       * platform implementation. If it is supported, HLS media will be played
       * normally. Otherwise we can't do anything with the current source and
       * the video will certainly not be displayed to the user.
       *
       * NOTE: `attachMedia` sets `video.src` to a `blob:` URL backed by a
       * MediaSource whose `sourceopen` has NOT fired yet (it is async).
       * The parent machine's `loadMedia` exit action checks for the blob
       * URL to avoid calling `videoElement.load()` which would abort this
       * async setup and silently prevent the m3u8 from loading.
       */
      handleHls:
        ({ videoElement, videoSrc, hls, sourceElement }) =>
        (callback: (event: LoadingSourcesEvent) => void) => {
          if (!(videoElement instanceof HTMLVideoElement) || !videoSrc) {
            return callback({ type: 'NATIVE' });
          }

          if (hasNativeHlsSupport(videoElement, sourceElement?.type)) {
            return callback({ type: 'NATIVE' });
          }

          if (!Hls.isSupported()) {
            return callback({ type: 'NATIVE' });
          }

          if (hls) {
            hls.stopLoad();
            hls.detachMedia();
          }

          const hlsInstance = new Hls();

          hlsInstance.loadSource(videoSrc);
          hlsInstance.attachMedia(videoElement);

          return callback({ type: 'POLYFILL', hlsInstance });
        },

      /**
       * Prefetch a source and create a `blob` object to be used as video source
       */
      async prefetch({ videoElement, videoSrc, abortController }) {
        if (!videoSrc) {
          return Promise.reject(new Error('[VideoPlayer] Missing video source for prefetch.'));
        }

        const controller = abortController ?? new AbortController();
        const dispatcher = videoElement ?? window;

        const res = await progressFetch.call(dispatcher, videoSrc, {
          signal: controller.signal,
        });

        const obj = await res.blob();

        return URL.createObjectURL(obj);
      },
    },
  },
);

/**
 * Determine if the `videoSrc` string is an HLS playlist source
 */
export function isHlsSrc({ videoSrc }: Pick<LoadingSourcesContext, 'videoSrc'>) {
  return Boolean(videoSrc && videoSrc.indexOf('.m3u') > 0);
}

function hasNativeHlsSupport(videoElement: HTMLVideoElement, sourceType?: string): boolean {
  return [sourceType, 'application/vnd.apple.mpegurl', 'application/x-mpegURL']
    .filter((type): type is string => Boolean(type))
    .some((type) => videoElement.canPlayType(type) !== '');
}

/**
 * Data that must be passed by the parent machine on invocation
 */
export const loadingSourcesMachineData = (context: LoadingSourcesContext) => ({
  abortController: context.abortController,
  hls: context.hls,
  plyr: context.plyr,
  posterSrc: context.posterSrc,
  prefetch: context.prefetch,
  sourceElement: context.sourceElement,
  videoElement: context.videoElement,
  videoSrc: context.videoSrc,
});
